import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isStockPack, getStockPackError } from '../../utils/stockPack';
import { FiCheck, FiRefreshCw } from 'react-icons/fi';
import VarietyEditorDialog from '../varietyPricing/VarietyEditorDialog';
import StockCostReferenceSummary, { calculatedCostForBasis, CostValues, referenceMoney } from './StockCostReferenceSummary';
import { calculateStockAmounts } from '../../utils/stockCost';
import { isFormatStockPurchase } from '../../utils/stockPurchaseFormat';
import { getEditableStandardStockCostRow } from './quickPriceListModel';
import {
  getStandardStockPurchaseGuidance,
  getStandardStockWeightError,
  isStandardGranelPurchase,
  getStockInputScope,
  STOCK_AMOUNT_GUIDANCE,
} from '../../utils/stockEntryGuidance';

export const COST_OVERRIDE_INPUT_FIELDS = Object.freeze([
  { key: 'price', label: 'Monto comprado con IVA', inputMode: 'numeric', step: '1', integer: true },
  { key: 'priceWithoutIva', label: 'Monto comprado sin IVA', inputMode: 'numeric', step: '1', integer: true },
  { key: 'quantity', label: 'Cantidad', inputMode: 'decimal', step: '0.001' },
  { key: 'metricUnit', label: 'Unidad de compra', type: 'text' },
  { key: 'weight', label: 'Peso declarado (kg)', inputMode: 'decimal', step: '0.001' },
  { key: 'weightKg', label: 'Peso normalizado (kg)', inputMode: 'decimal', step: '0.001' },
  { key: 'lengthCm', label: 'Largo (cm)', inputMode: 'decimal', step: '0.001' },
  { key: 'widthCm', label: 'Ancho (cm)', inputMode: 'decimal', step: '0.001' },
  { key: 'heightCm', label: 'Alto (cm)', inputMode: 'decimal', step: '0.001' },
  { key: 'bultosPerBin', label: 'Bultos por bin', inputMode: 'numeric', step: '1', integer: true },
  { key: 'unitsPerPack', label: 'Unidades por pack', inputMode: 'numeric', step: '1', integer: true },
]);

export const STANDARD_COST_OVERRIDE_INPUT_FIELDS = Object.freeze(
  [...COST_OVERRIDE_INPUT_FIELDS.slice(0, 5), COST_OVERRIDE_INPUT_FIELDS.find((field) => field.key === 'unitsPerPack')].map((field) => (
    field.key === 'weight' ? { ...field, label: 'Peso bruto (kg)' } : field
  ))
);

const COST_OVERRIDE_FIELD_KEYS = new Set(COST_OVERRIDE_INPUT_FIELDS.map(({ key }) => key));

const isPresent = (value) => value !== null && value !== undefined && String(value).trim() !== '';
const toInputValue = (value) => (isPresent(value) ? String(value) : '');

const getSnapshotDisplayWeight = (inputs, originalInputs = inputs, latestEdit = null) => {
  const positive = (value) => Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : null;
  if (latestEdit) {
    if (positive(inputs.weightKg) !== positive(originalInputs.weightKg)) return positive(inputs.weightKg);
    if (positive(inputs.weight) !== positive(originalInputs.weight)) return positive(inputs.weight);
  }
  return positive(inputs.weightKg) ?? positive(inputs.purchaseSnapshot?.weightKg) ?? positive(inputs.weight);
};

const normalizeInputRecord = (value = {}, fields = COST_OVERRIDE_INPUT_FIELDS) => fields.reduce((result, field) => {
  result[field.key] = toInputValue(value?.[field.key]);
  return result;
}, {});

const getDirectInputs = (row = {}, fields = COST_OVERRIDE_INPUT_FIELDS) => fields.reduce((result, field) => {
  if (Object.hasOwn(row, field.key)) result[field.key] = row[field.key];
  return result;
}, {});

const getCostOverrideInputFields = (breakdown = {}) => (
  breakdown?.listType === 'standard'
    ? STANDARD_COST_OVERRIDE_INPUT_FIELDS
    : COST_OVERRIDE_INPUT_FIELDS
);

const getEditableRows = (breakdown = {}) => {
  if (breakdown?.listType === 'standard') {
    const editableSourceRow = getEditableStandardStockCostRow(breakdown);
    return editableSourceRow ? [editableSourceRow] : [];
  }
  return Array.isArray(breakdown?.rows) ? breakdown.rows : [];
};

export const normalizeCostOverrideRows = (breakdown = {}) => {
  const fields = getCostOverrideInputFields(breakdown);
  return getEditableRows(breakdown).map((row, index) => {
    const currentSource = row?.inputs && typeof row.inputs === 'object'
      ? row.inputs
      : getDirectInputs(row, fields);
    const originalSource = row?.originalInputs && typeof row.originalInputs === 'object'
      ? row.originalInputs
      : isStockPack(currentSource?.metricUnit)
        ? { ...currentSource, weight: currentSource.weightKg ?? currentSource.weight }
        : currentSource;
    // Keep format identity for validation and guidance, outside the editable
    // fields so it is never submitted as a cost correction.
    const purchaseMode = breakdown?.listType === 'standard'
      && isFormatStockPurchase({ ...row, ...currentSource })
      ? currentSource.purchaseMode ?? row.purchaseMode : undefined;
    const standardGranel = breakdown?.listType === 'standard'
      && isStandardGranelPurchase({ metricUnit: currentSource?.metricUnit, purchaseMode });
    const currentExplicitWeight = Number(currentSource?.weightKg);
    const originalExplicitWeight = Number(originalSource?.weightKg);
    const currentWeight = Number(currentSource?.weight);
    const originalWeight = Number(originalSource?.weight);
    const hasCorrectedWeight = Boolean(
      row?.latestEdit
      && Number.isFinite(currentWeight)
      && currentWeight > 0
      && (!Number.isFinite(originalWeight) || currentWeight !== originalWeight)
    );
    const hasExplicitWeight = Number.isFinite(currentExplicitWeight) && currentExplicitWeight > 0;
    const legacyGranel = standardGranel && !hasExplicitWeight && !hasCorrectedWeight;
    const multiplyAmount = (value, quantity) => {
      if (!isPresent(value)) return value;
      const parsedValue = Number(value);
      const parsedQuantity = Number(quantity);
      return Number.isFinite(parsedValue) && Number.isFinite(parsedQuantity)
        ? parsedValue * parsedQuantity
        : value;
    };
    const currentDisplaySource = standardGranel
      ? {
          ...currentSource,
          price: legacyGranel
            ? multiplyAmount(currentSource?.price, currentSource?.quantity)
            : currentSource?.price,
          priceWithoutIva: legacyGranel
            ? multiplyAmount(currentSource?.priceWithoutIva, currentSource?.quantity)
            : currentSource?.priceWithoutIva,
          weight: hasExplicitWeight
            ? currentExplicitWeight
            : hasCorrectedWeight
              ? currentSource?.weight
              : currentSource?.quantity,
        }
      : purchaseMode
        ? { ...currentSource, weight: getSnapshotDisplayWeight(currentSource, originalSource, row.latestEdit) }
        : currentSource;
    const originalGranel = breakdown?.listType === 'standard'
      && isStandardGranelPurchase({ metricUnit: originalSource?.metricUnit, purchaseMode });
    const originalLegacyGranel = originalGranel
      && !(Number.isFinite(originalExplicitWeight) && originalExplicitWeight > 0);
    const originalDisplaySource = originalGranel
      ? {
          ...originalSource,
          price: originalLegacyGranel
            ? multiplyAmount(originalSource?.price, originalSource?.quantity)
            : originalSource?.price,
          priceWithoutIva: originalLegacyGranel
            ? multiplyAmount(originalSource?.priceWithoutIva, originalSource?.quantity)
            : originalSource?.priceWithoutIva,
          weight: Number.isFinite(originalExplicitWeight) && originalExplicitWeight > 0
            ? originalExplicitWeight
            : originalSource?.quantity,
        }
      : purchaseMode
        ? { ...originalSource, weight: getSnapshotDisplayWeight(originalSource) }
        : isStockPack(originalSource?.metricUnit)
        ? { ...originalSource, weight: originalSource.weightKg ?? originalSource.weight }
        : originalSource;
    return {
      key: `${row?.stockId ?? 'missing'}:${index}`,
      stockId: row?.stockId ?? null,
      purchaseMode,
      label: row?.varietyName || (row?.stockId ? `Registro #${row.stockId}` : `Registro ${index + 1}`),
      originalInputs: normalizeInputRecord(originalDisplaySource, fields),
      currentInputs: normalizeInputRecord(currentDisplaySource, fields),
      proposedInputs: normalizeInputRecord(currentDisplaySource, fields),
      latestEdit: row?.latestEdit || null,
      legacyGranel,
    };
  });
};

const normalizePatchValue = (key, value) => {
  if (!COST_OVERRIDE_FIELD_KEYS.has(key)) return undefined;
  if (key === 'metricUnit') return String(value || '').trim() || null;
  if (!isPresent(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
};

export const buildCostOverrideEdits = (
  rows = [],
  fields = COST_OVERRIDE_INPUT_FIELDS,
  { standardStockCorrection = false } = {}
) => rows.reduce((edits, row) => {
  if (row?.stockId === null || row?.stockId === undefined) return edits;
  const patch = {};
  fields.forEach(({ key }) => {
    if (standardStockCorrection
      && key === 'quantity'
      && isStandardGranelPurchase({ metricUnit: row.proposedInputs?.metricUnit, purchaseMode: row.purchaseMode })) return;
    const current = normalizePatchValue(key, row.currentInputs?.[key]);
    const proposed = normalizePatchValue(key, row.proposedInputs?.[key]);
    if (!Object.is(current, proposed)) patch[key] = proposed;
  });
  const proposedIsGranel = standardStockCorrection
    && isStandardGranelPurchase({ metricUnit: row.proposedInputs?.metricUnit, purchaseMode: row.purchaseMode });
  const currentIsGranel = standardStockCorrection
    && isStandardGranelPurchase({ metricUnit: row.currentInputs?.metricUnit, purchaseMode: row.purchaseMode });
  const activatesGranelWeight = proposedIsGranel
    && (!currentIsGranel || Object.hasOwn(patch, 'weight'));
  if (proposedIsGranel && row.legacyGranel && Object.keys(patch).length) {
    ['price', 'priceWithoutIva', 'weight'].forEach((key) => {
      patch[key] = normalizePatchValue(key, row.proposedInputs?.[key]);
    });
    patch.quantity = 1;
  }
  if (activatesGranelWeight && Number(row.currentInputs?.quantity) !== 1) {
    patch.quantity = 1;
  }
  if (Object.keys(patch).length) edits.push({ stockId: row.stockId, patch });
  return edits;
}, []);

export const validateProposedRows = (
  rows = [],
  fields = COST_OVERRIDE_INPUT_FIELDS,
  { standardStockCorrection = false } = {}
) => {
  for (const row of rows) {
    const packError = getStockPackError({ ...row.proposedInputs, purchaseMode: row.purchaseMode });
    if (packError) return packError;
    for (const field of fields) {
      if (standardStockCorrection
        && field.key === 'quantity'
        && isStandardGranelPurchase({ metricUnit: row.proposedInputs?.metricUnit, purchaseMode: row.purchaseMode })) continue;
      const value = row.proposedInputs?.[field.key];
      if (field.key === 'metricUnit' || !isPresent(value)) continue;
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed < 0) {
        return `${field.label} de ${row.label} debe ser un número igual o mayor que cero.`;
      }
      if (field.integer && !Number.isInteger(parsed)) {
        return `${field.label} de ${row.label} debe ser un número entero.`;
      }
      if (field.key === 'quantity' && parsed <= 0) {
        return `Cantidad de ${row.label} debe ser mayor que cero.`;
      }
      if (field.key === 'bultosPerBin' && parsed < 1) {
        return `Bultos por bin de ${row.label} debe ser un entero mayor que cero.`;
      }
    }
    if (standardStockCorrection) {
      if (row.purchaseMode && !(Number(row.proposedInputs?.weight) > 0)) {
        return `El peso por formato debe ser mayor a 0 kg para ${row.label}.`;
      }
      const standardWeightError = getStandardStockWeightError({
        metricUnit: row.proposedInputs?.metricUnit,
        weight: row.proposedInputs?.weight,
        purchaseMode: row.purchaseMode,
      });
      if (standardWeightError) {
        return `${standardWeightError.replace(/\.$/, '')} para ${row.label}.`;
      }
    }
    const price = Number(row.proposedInputs?.price);
    const priceWithoutIva = Number(row.proposedInputs?.priceWithoutIva);
    if (Number.isFinite(price) && Number.isFinite(priceWithoutIva)
      && price + priceWithoutIva <= 0) {
      return `El costo de compra de ${row.label} debe ser mayor que cero.`;
    }
  }
  return '';
};

const getErrorMessage = (error, fallback) => error?.response?.data?.message
  || error?.message
  || fallback;

const getBasisLabel = (basis) => {
  if (basis === 'kg') return 'Por kg';
  if (basis === 'unit') return 'Por unidad';
  return basis ? String(basis) : 'Sin base';
};

const formatRawValue = (key, value) => {
  if (!isPresent(value)) return 'Sin dato';
  if (key === 'metricUnit') return String(value);
  if (key === 'price' || key === 'priceWithoutIva') return referenceMoney(value);
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? parsed.toLocaleString('es-CL', { maximumFractionDigits: 3 })
    : String(value);
};

const CostOverrideDialog = ({
  open = false,
  productLabel = '',
  varietyLabel = '',
  formatLabel = '',
  manualBaseCost = '',
  basis,
  costs = {},
  onPreview,
  onApply,
  onCancel,
  busy = false,
  error = '',
}) => {
  // Freeze all references together for the duration of a review. A background
  // catalog refresh must not relabel the user's baseline or replace their draft.
  const snapshotRef = useRef(null);
  if (!open) snapshotRef.current = null;
  if (open && !snapshotRef.current) snapshotRef.current = JSON.parse(JSON.stringify(costs || {}));
  const referenceCosts = snapshotRef.current || costs || {};
  const breakdown = referenceCosts.referenceCostBreakdown || {};
  const standardStockCorrection = breakdown?.listType === 'standard';
  const sourceStockListId = breakdown?.sourceStockListId ?? referenceCosts.sourceStockListId ?? null;
  const declaredBasis = referenceCosts.pricingBasis || basis || referenceCosts.manualBaseCostBasis || referenceCosts.basis || breakdown?.pricingBasis;
  const inputFields = standardStockCorrection
    ? STANDARD_COST_OVERRIDE_INPUT_FIELDS
    : COST_OVERRIDE_INPUT_FIELDS;
  const initialRows = useMemo(() => normalizeCostOverrideRows(breakdown), [breakdown]);
  const identity = `${sourceStockListId ?? 'none'}:${breakdown?.contextVersion
    ?? referenceCosts.referenceCostContextVersion
    ?? formatLabel}`;
  const [rows, setRows] = useState(initialRows);
  const [manualDraft, setManualDraft] = useState(toInputValue(manualBaseCost));
  const [preview, setPreview] = useState(null);
  const [action, setAction] = useState('');
  const [localError, setLocalError] = useState('');
  const initializedIdentityRef = useRef('');
  const operationRef = useRef(null);
  const operationSequenceRef = useRef(0);
  const previewHeadingRef = useRef(null);

  useEffect(() => {
    if (!open) {
      initializedIdentityRef.current = '';
      operationRef.current = null;
      operationSequenceRef.current += 1;
      return;
    }
    if (initializedIdentityRef.current === identity) return;
    initializedIdentityRef.current = identity;
    operationRef.current = null;
    operationSequenceRef.current += 1;
    setRows(initialRows);
    setManualDraft(toInputValue(manualBaseCost));
    setPreview(null);
    setAction('');
    setLocalError('');
  }, [identity, initialRows, manualBaseCost, open]);

  useEffect(() => {
    if (!preview) return;
    previewHeadingRef.current?.focus();
  }, [preview]);

  const effectiveBusy = Boolean(busy || action);
  const externalError = typeof error === 'string' ? error : getErrorMessage(error, 'No se pudo completar la operación.');
  const visibleError = localError || externalError;
  const edits = useMemo(
    () => buildCostOverrideEdits(rows, inputFields, { standardStockCorrection }),
    [inputFields, rows, standardStockCorrection]
  );
  const allowedFieldKeys = useMemo(
    () => new Set(inputFields.map(({ key }) => key)),
    [inputFields]
  );

  const patchProposedInput = (rowKey, fieldKey, value) => {
    if (effectiveBusy || !allowedFieldKeys.has(fieldKey)) return;
    setRows((current) => current.map((row) => {
      if (row.key !== rowKey) return row;
      const proposedInputs = { ...row.proposedInputs, [fieldKey]: value };
      const entersStandardGranel = standardStockCorrection
        && fieldKey === 'metricUnit'
        && !isStandardGranelPurchase({ metricUnit: row.proposedInputs?.metricUnit, purchaseMode: row.purchaseMode })
        && isStandardGranelPurchase({ metricUnit: value, purchaseMode: row.purchaseMode });
      if (entersStandardGranel) {
        const quantity = Number(row.proposedInputs?.quantity);
        const multiplier = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
        ['price', 'priceWithoutIva', 'weight'].forEach((key) => {
          if (!isPresent(row.proposedInputs?.[key])) return;
          const parsed = Number(row.proposedInputs[key]);
          if (Number.isFinite(parsed)) proposedInputs[key] = String(parsed * multiplier);
        });
        proposedInputs.quantity = '1';
      }
      return { ...row, proposedInputs };
    }));
    setPreview(null);
    setLocalError('');
  };

  const runOperation = async (operationName, callback) => {
    if (busy || operationRef.current) return null;
    const operationId = ++operationSequenceRef.current;
    operationRef.current = operationName;
    setAction(operationName);
    setLocalError('');
    try {
      return await callback();
    } catch (operationError) {
      if (operationSequenceRef.current === operationId) {
        setLocalError(getErrorMessage(
          operationError,
          operationName === 'preview'
            ? 'No se pudo recalcular este costo.'
            : 'No se pudo aplicar este costo.'
        ));
      }
      return null;
    } finally {
      if (operationSequenceRef.current === operationId) {
        operationRef.current = null;
        setAction('');
      }
    }
  };

  const handlePreview = () => runOperation('preview', async () => {
    const validationError = validateProposedRows(rows, inputFields, { standardStockCorrection });
    if (validationError) {
      setLocalError(validationError);
      return null;
    }
    if (sourceStockListId === null || sourceStockListId === undefined || !initialRows.length) {
      setLocalError('Este costo no tiene una compra de origen editable.');
      return null;
    }
    if (typeof onPreview !== 'function') return null;
    const result = await onPreview({ sourceStockListId, edits });
    const resolved = result?.data || result;
    if (!resolved || !isPresent(resolved.recommendedCost) || !Number.isFinite(Number(resolved.recommendedCost)) || !resolved.contextToken) {
      throw new Error('La vista previa no entregó un costo recomendado válido.');
    }
    setPreview(resolved);
    return resolved;
  });

  const handleApply = (requestedChoice) => runOperation('apply', async () => {
    if (!preview?.contextToken || typeof onApply !== 'function') return null;
    const choice = standardStockCorrection ? 'recommended' : requestedChoice;
    const payload = {
      sourceStockListId,
      edits,
      choice,
      contextToken: preview.contextToken,
    };
    if (choice === 'manual') {
      const parsedManualCost = Number(manualDraft);
      if (!isPresent(manualDraft) || !Number.isInteger(parsedManualCost) || parsedManualCost < 0) {
        setLocalError('El costo manual debe ser un número entero igual o mayor que cero.');
        return null;
      }
      payload.manualBaseCost = parsedManualCost;
    }
    return onApply(payload);
  });

  const handleCancel = () => {
    if (!effectiveBusy) onCancel?.();
  };

  const titleParts = [productLabel, varietyLabel, formatLabel].filter(Boolean);
  const title = titleParts.join(' · ') || 'Costo calculado';

  return (
    <VarietyEditorDialog
      isOpen={open}
      eyebrow="Revisión de costo"
      title={title}
      description={standardStockCorrection
        ? 'Corrige el último ingreso de compra, revisa el nuevo cálculo y confirma el costo recomendado.'
        : 'Revisa la compra que originó el costo, prueba cambios y decide qué costo usar.'}
      onClose={handleCancel}
      busy={effectiveBusy}
      closeLabel="Cerrar revisión de costo"
      footerNote="La compra original queda registrada. Los cambios se aplican solo después de tu confirmación."
      footerActions={preview ? (
        <>
          <button
            type="button"
            disabled={effectiveBusy}
            onClick={handleCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-white disabled:opacity-50"
          >
            Cancelar
          </button>
          {!standardStockCorrection && (
            <button
              type="button"
              disabled={effectiveBusy}
              onClick={() => handleApply('manual')}
              className="rounded-lg border border-blue-300 bg-white px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {action === 'apply' ? 'Aplicando…' : 'Mantener costo ingresado'}
            </button>
          )}
          <button
            type="button"
            disabled={effectiveBusy}
            onClick={() => handleApply('recommended')}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiCheck aria-hidden="true" />
            {action === 'apply' ? 'Aplicando…' : 'Usar costo recomendado'}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            disabled={effectiveBusy}
            onClick={handleCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-white disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={effectiveBusy || !initialRows.length || !edits.length
              || sourceStockListId === null || sourceStockListId === undefined}
            onClick={handlePreview}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiRefreshCw aria-hidden="true" className={action === 'preview' ? 'animate-spin' : ''} />
            {action === 'preview' ? 'Recalculando…' : 'Recalcular este costo'}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <StockCostReferenceSummary costs={referenceCosts} formatLabel={formatLabel} />
        <section className="rounded-xl border border-slate-200 bg-slate-50 p-4" aria-labelledby="cost-source-heading">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 id="cost-source-heading" className="text-sm font-bold text-slate-950">Compra de origen</h3>
              <p className="mt-0.5 text-xs text-slate-500">
                {breakdown?.sourceStockListName || (sourceStockListId ? `Lista #${sourceStockListId}` : 'Sin compra de origen')}
              </p>
            </div>
            <div className="text-xs text-slate-600">Costo calculado · {getBasisLabel(declaredBasis || 'unit')}
              <p className="font-semibold text-slate-950">{referenceMoney(calculatedCostForBasis({ ...referenceCosts, pricingBasis: declaredBasis }, declaredBasis || 'unit'))}</p>
            </div>
          </div>
          <div className="mt-3"><CostValues values={[
            { label: 'Compra base', value: breakdown.totalBaseCost, scope: 'Total de esta variedad en la lista' },
            { label: 'Gastos asignados', value: breakdown.totalAllocatedExpenses, scope: 'Total asignado a esta variedad' },
            { label: 'Costo total de la variedad', value: breakdown.totalReferenceCost, scope: 'Compra base + gastos asignados' },
            { label: 'Gastos totales de la lista', value: breakdown.totalListExpenses, scope: 'Todas las variedades de la lista' },
          ]} /></div>
          <p className="mt-3 text-xs text-slate-600">
            {breakdown.allocationBasis === 'weight' ? 'Los gastos se asignan proporcionalmente a los kilos comprados.'
              : breakdown.allocationBasis === 'quantity' ? 'Los gastos se asignan proporcionalmente a las cantidades compradas.'
                : breakdown.listType === 'importaciones' ? 'Cada gasto de importación se distribuye según su base: peso, volumen o monto de compra.'
                  : 'La base de distribución de los gastos no está disponible.'}
            {' '}Los gastos operacionales se consultan aquí; se editan desde la lista de stock.
          </p>
          {Array.isArray(breakdown.expenses) && breakdown.expenses.length > 0 && (
            <details className="mt-3 text-xs text-slate-600">
              <summary className="cursor-pointer font-semibold">Distribución de gastos de importación</summary>
              <ul className="mt-2 space-y-2">
                {breakdown.expenses.map((expense, index) => (
                  <li key={expense.expenseId ?? index}>
                    <strong>{expense.title || 'Gasto'}:</strong> {referenceMoney(expense.amount)} en toda la lista;{' '}
                    {referenceMoney(expense.expenseShare)} asignados a esta variedad. Base: {' '}
                    {{ weight: 'kilos comprados', volume: 'volumen comprado', price: 'monto de compra' }[expense.allocationBasis] || 'Sin dato'}.
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>

        {!rows.length ? (
          <p className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            No hay registros de compra editables para este costo.
          </p>
        ) : rows.map((row) => {
          const standardPurchaseGuidance = standardStockCorrection
            ? getStandardStockPurchaseGuidance({ metricUnit: row.proposedInputs?.metricUnit, purchaseMode: row.purchaseMode })
            : null;
          const standardGranel = standardStockCorrection
            && isStandardGranelPurchase({ metricUnit: row.proposedInputs?.metricUnit, purchaseMode: row.purchaseMode });
          const proposedAmounts = calculateStockAmounts({ ...row.proposedInputs,
            quantity: standardGranel ? 1 : row.proposedInputs.quantity });
          const guidanceId = `cost-row-${row.key}-purchase-guidance`.replace(/[^a-zA-Z0-9-_]/g, '-');
          return (
          <section key={row.key} className="overflow-hidden rounded-xl border border-slate-200" aria-labelledby={`cost-row-${row.key}`}>
            <header className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-4 py-3">
              <div>
                <h3 id={`cost-row-${row.key}`} className="text-sm font-bold text-slate-950">{row.label}</h3>
                <p className="text-xs text-slate-500">
                  {row.stockId ? `Registro de stock #${row.stockId}` : 'Registro sin identificador'}
                </p>
              </div>
              {row.latestEdit && (
                <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700">
                  Editado previamente
                </span>
              )}
            </header>
            <div className="divide-y divide-slate-100">
              <div className="hidden grid-cols-[minmax(10rem,1fr)_minmax(6rem,0.8fr)_minmax(6rem,0.8fr)_minmax(11rem,1fr)] gap-3 bg-white px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 md:grid">
                <span>Dato de cálculo</span>
                <span>Original</span>
                <span>Actual</span>
                <span>Propuesto</span>
              </div>
              {inputFields.filter((field) => !(standardGranel && field.key === 'quantity') && (field.key !== 'unitsPerPack' || (!row.purchaseMode && isStockPack(row.proposedInputs?.metricUnit)))).map((field) => {
                const inputId = `cost-override-${row.key}-${field.key}`.replace(/[^a-zA-Z0-9-_]/g, '-');
                const isGuidedWeightField = Boolean(standardPurchaseGuidance && field.key === 'weight');
                const isBinWeightField = Boolean(
                  isGuidedWeightField
                  && !row.purchaseMode
                  && String(row.proposedInputs.metricUnit).trim().toLowerCase() === 'bin'
                );
                const fieldLabel = isGuidedWeightField
                  ? standardPurchaseGuidance.weightLabel
                  : field.label;
                const scopeOptions = { isImportList: !standardStockCorrection, metricUnit: row.proposedInputs.metricUnit, purchaseMode: row.purchaseMode };
                const minimum = field.type === 'text'
                  ? undefined
                  : isBinWeightField
                    ? '1'
                    : (standardGranel || row.purchaseMode) && field.key === 'weight'
                      ? '0.001'
                      : '0';
                return (
                  <div key={field.key} className="grid grid-cols-2 gap-2 px-4 py-2.5 md:grid-cols-[minmax(10rem,1fr)_minmax(6rem,0.8fr)_minmax(6rem,0.8fr)_minmax(11rem,1fr)] md:items-center md:gap-3">
                    <label htmlFor={inputId} className="col-span-2 text-xs font-semibold text-slate-700 md:col-span-1">{fieldLabel}</label>
                    {['originalInputs', 'currentInputs'].map((source, index) => (
                      <div key={source} className="text-xs text-slate-600" aria-label={`${index === 0 ? 'Original' : 'Actual'} de ${field.label} para ${row.label}`}>
                        <span className="mr-1 font-medium text-slate-400 md:hidden">{index === 0 ? 'Original' : 'Actual'}:</span>
                        <span>{formatRawValue(field.key, row[source][field.key])}</span>
                        {field.key !== 'metricUnit' && <p className="mt-1 text-[11px] text-slate-500">{getStockInputScope({ ...scopeOptions, metricUnit: row[source].metricUnit }, field.key)}</p>}
                      </div>
                    ))}
                    <div className="col-span-2 md:col-span-1">
                    <span className="text-xs font-medium text-slate-500 md:hidden">Propuesto:</span>
                    <input
                      id={inputId}
                      type={field.type || 'number'}
                      inputMode={isBinWeightField ? 'numeric' : field.inputMode}
                      min={minimum}
                      step={isBinWeightField ? '1' : field.step}
                      value={row.proposedInputs[field.key]}
                      disabled={effectiveBusy || row.stockId === null || row.stockId === undefined}
                      onChange={(event) => patchProposedInput(row.key, field.key, event.target.value)}
                      aria-label={`Valor propuesto de ${fieldLabel} para ${row.label}`}
                      aria-describedby={`${inputId}-scope ${guidanceId}`}
                      className={`${field.type === 'text' ? '' : 'no-number-spinner '}w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-slate-100`}
                    />
                    <p id={`${inputId}-scope`} className="mt-1 text-[11px] text-slate-500">{getStockInputScope(scopeOptions, field.key)}</p>
                    </div>
                  </div>
                );
              })}
              <div id={guidanceId} className="bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900">
                <p>{STOCK_AMOUNT_GUIDANCE}</p>
                {standardPurchaseGuidance && <>
                  <p>{standardPurchaseGuidance.amount}</p>
                  <p className="mt-1">{standardPurchaseGuidance.weight}</p>
                </>}
              </div>
              <div className="px-4 py-3 text-xs text-slate-600">
                <p>Total propuesto de este ingreso al proveedor: <strong>{referenceMoney(proposedAmounts.providerTotalAmount)}</strong></p>
                <p>Costo total propuesto de este ingreso, antes de gastos: <strong>{referenceMoney(proposedAmounts.stockCostAmount)}</strong></p>
              </div>
            </div>
          </section>
          );
        })}

        {rows.length > 0 && edits.length === 0 && !preview && (
          <p className="text-xs text-slate-500">Modifica al menos un dato de la compra para recalcular este costo.</p>
        )}

        {!standardStockCorrection && (
          <section className="rounded-xl border border-blue-200 bg-blue-50 p-4" aria-labelledby="manual-cost-heading">
            <h3 id="manual-cost-heading" className="text-sm font-bold text-blue-950">Costo manual ingresado</h3>
            <p className="mt-0.5 text-xs text-blue-800">
              Puedes conservar este valor después de revisar la recomendación.
            </p>
            <label className="mt-3 block max-w-xs text-xs font-semibold text-blue-950" htmlFor="cost-override-manual-cost">
              Costo manual{declaredBasis ? ` · ${getBasisLabel(declaredBasis)}` : ''}
            </label>
            <div className="relative mt-1 max-w-xs">
              <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-slate-500">$</span>
              <input
                id="cost-override-manual-cost"
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                value={manualDraft}
                disabled={effectiveBusy}
                onChange={(event) => {
                  setManualDraft(event.target.value);
                  setLocalError('');
                }}
                className="no-number-spinner w-full rounded-md border border-blue-300 bg-white py-2 pl-7 pr-3 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-blue-300 disabled:cursor-not-allowed disabled:bg-slate-100"
              />
            </div>
          </section>
        )}

        {preview && (
          <section
            ref={previewHeadingRef}
            tabIndex={-1}
            aria-live="polite"
            className="rounded-xl border border-emerald-300 bg-emerald-50 p-4 outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Vista previa lista</p>
            <h3 className="mt-1 text-base font-bold text-emerald-950">Costo recomendado: {referenceMoney(preview.recommendedCost)}</h3>
            <p className="mt-1 text-sm text-emerald-800">Base de cálculo: {getBasisLabel(preview.basis)}</p>
            <p className="mt-1 text-sm text-emerald-800">Costo calculado antes de editar ({getBasisLabel(preview.basis)}): {referenceMoney(calculatedCostForBasis({ ...referenceCosts, pricingBasis: declaredBasis }, preview.basis))}</p>
            {preview.breakdown?.totalBaseCost !== null && preview.breakdown?.totalBaseCost !== undefined && (
              <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-white/70 p-2">
                  <dt className="text-emerald-700">Compra base · Total de esta variedad</dt>
                  <dd className="font-semibold text-emerald-950">{referenceMoney(preview.breakdown.totalBaseCost)}</dd>
                </div>
                <div className="rounded-lg bg-white/70 p-2">
                  <dt className="text-emerald-700">Gastos asignados · Total de esta variedad</dt>
                  <dd className="font-semibold text-emerald-950">{referenceMoney(preview.breakdown.totalAllocatedExpenses)}</dd>
                </div>
              </dl>
            )}
          </section>
        )}

        {visibleError && (
          <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {visibleError}
          </p>
        )}
      </div>
    </VarietyEditorDialog>
  );
};

export default CostOverrideDialog;
